// Закрытие объекта. Модалка — ровно тот случай, для которого она и оставлена (правило 4):
// необратимое для человека действие, которое надо подтвердить. Объект не удаляется, он
// перестаёт быть действующим: PATCH { is_active: false } (сценарий 16).
//
// Подтверждение обязано называть ПОСЛЕДСТВИЕ, а не спрашивать «вы уверены». Уверенность
// человека не информация; «пропадёт из активных списков, история останется» — информация.
//
// Нет права на правку справочников — кнопка всё равно нажимается и объясняет, чего не
// хватает (правило 10). Выключенная кнопка не доводит нажатие до обработчика, и со стороны
// это неотличимо от сломанной программы.

import { useState } from 'react';

import { RiLockLine, RiShutDownLine } from '@remixicon/react';

import type { SiteOut } from '@/shared/api/endpoints/org';
import { useAuthStore } from '@/shared/auth/store';
import { can } from '@/shared/rbac/permissions';
import * as Button from '@/shared/ui/button';
import { ErrorAlert } from '@/shared/ui/error-alert';
import * as Modal from '@/shared/ui/modal';

import { usePatchSiteMutation } from '@/shared/org/queries';

/** Заведение и правка объектов — право на справочники (server/app/api/v1/dictionaries.py). */
const EDIT_PERMISSION = 'core.dictionary.edit';

export function CloseSiteDialog({ site }: { site: SiteOut }) {
  const [open, setOpen] = useState(false);
  const permissions = useAuthStore((state) => state.permissions);
  const allowed = can(permissions, EDIT_PERMISSION);
  const mutation = usePatchSiteMutation(site.id);

  function confirm() {
    mutation.mutate(
      { is_active: false },
      {
        onSuccess: () => {
          setOpen(false);
        },
      },
    );
  }

  return (
    <Modal.Root
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) {
          mutation.reset();
        }
      }}
    >
      <Modal.Trigger asChild>
        <Button.Root variant='error' mode='stroke' size='xsmall' type='button'>
          <Button.Icon as={RiShutDownLine} />
          Закрыть объект
        </Button.Root>
      </Modal.Trigger>
      <Modal.Content>
        <Modal.Header
          icon={allowed ? RiShutDownLine : RiLockLine}
          title={allowed ? `Закрыть объект «${site.name}»?` : 'Закрыть объект нельзя'}
          description={
            allowed
              ? 'Объект перестанет быть действующим прямо сейчас.'
              : 'Объектами распоряжается тот, у кого есть право на правку справочников.'
          }
        />
        <Modal.Body>
          {allowed ? (
            <ul className='flex flex-col gap-1.5 text-paragraph-sm text-text-sub-600'>
              <li>Объект пропадёт из активных списков и из подбора при назначении людей.</li>
              <li>Вся история останется: табели, ведомости, посты и назначения не удаляются.</li>
              <li>Найти закрытый объект можно в реестре — он будет помечен как «закрыт».</li>
            </ul>
          ) : (
            <p className='text-paragraph-sm text-text-sub-600'>
              У вашей учётной записи нет права на правку справочников, поэтому закрыть объект не
              выйдет. Попросите администратора выдать это право — либо передайте закрытие тому, у
              кого оно есть.
            </p>
          )}
          {mutation.isError && <ErrorAlert error={mutation.error} className='mt-3' />}
        </Modal.Body>
        <Modal.Footer>
          <Modal.Close asChild>
            <Button.Root variant='neutral' mode='stroke' size='small' className='w-full'>
              {allowed ? 'Отмена' : 'Понятно'}
            </Button.Root>
          </Modal.Close>
          {allowed && (
            <Button.Root
              variant='error'
              mode='filled'
              size='small'
              className='w-full'
              type='button'
              onClick={confirm}
            >
              {mutation.isPending ? 'Закрываем…' : 'Закрыть объект'}
            </Button.Root>
          )}
        </Modal.Footer>
      </Modal.Content>
    </Modal.Root>
  );
}
